import { IApiRepository } from 'service/protocols/api/api-repository'
import { SurgicalOrderRepository as IReportsRepository } from 'repository/interfaces/surgical-order-repository'
import { AddSurgicalOrder } from 'domain/usecases/surgical-order/add-surgical-order'
import { ApiStatusCode } from 'service/protocols/api/api-client'
import handleGraphQLError from 'repository/graphql/utils/handle-error'
import { RepositoryErrors } from 'repository/errors/repository-errors'
import { LoadCid } from 'domain/usecases/surgical-order/load-cid'
import { LoadTuss } from 'domain/usecases/surgical-order/load-tuss'
import {
  countSurgicalOrdersByStatus,
  getCidQuery,
  getTussQuery,
  loadFavoriteDoctorOrdersQuery,
  loadFavoriteSurgicalOrdersQuery,
  loadSurgeriesQuery,
  loadSurgicalOrderQuery,
  loadSurgicalOrderStatusQuery,
  searchSurgicalOrdersQuery,
  countScheduledAndUnscheduledSurgicalOrders,
  loadGuidesByType
} from 'repository/graphql/queries'
import { RegisterSurgicalOrder } from 'domain/usecases/surgical-order/register-surgical-order'
import {
  addFavoriteMutation,
  checkinMutation,
  concludeSurgicalOrderMutation,
  confirmMergeDocumentsMutation,
  createPatientSurgicalOrderRequestMutation,
  deleteFavoritedSurgicalOrderMutation,
  deleteSurgicalOrderSolicitationMutation,
  deleteUploadedDocumentsMutation,
  registerCrmoSurgicalOrderMutation,
  registerSecretarySurgicalOrderMutation,
  registerSurgicalOrderMutation,
  reorderSurgeryMutation,
  updateSurgeryStatusMutation,
  updateSurgicalOrderMutation,
  updateSurgicalOrderRegenerateGuidesMutation
} from 'repository/graphql/mutations'
import { makeGraphQLVariable } from 'repository/graphql/utils/make-variables'
import { LoadSurgicalOrder } from 'domain/usecases/surgical-order/load-surgical-order'
import { AddFavorite } from 'domain/usecases/surgical-order/add-favorite'
import { UploadSurgicalOrderDocument } from 'domain/usecases/surgical-order/upload-surgical-order-document'
import { SearchSurgicalOrders } from 'domain/usecases/surgical-order/search-surgical-orders'
import { LoadSurgicalOrderStatus } from 'domain/usecases/surgical-order/get-surgical-order-status'
import { ChangeSurgicalOrderStatus } from 'domain/usecases/surgical-order/change-surgical-order-status'
import { ReorderSurgicalOrder } from 'domain/usecases/surgical-order/reorder-surgery'
import { UpdateSurgicalOrder } from 'domain/usecases/surgical-order/update-surgical-order'
import { CreateSurgicalOrderStatus } from 'domain/usecases/surgical-order/create-surgical-order-status'
import { LoadSurgeries } from 'domain/usecases/surgery/load-surgeries'
import { LoadSurgicalOrderDocument } from 'domain/usecases/surgical-order/load-surgical-order-document'
import { CountSurgicalOrdersByStatus } from 'domain/usecases/surgical-order/count-surgical-orders-by-status'
import { ConcludeSurgicalOrder } from 'domain/usecases/surgical-order/conclude-surgical-order'
import { DeleteSurgicalOrderSolicitation } from 'domain/usecases/surgical-order/delete-surgical-order-solicitation'
import { UploadSurgicalOrderDocumentsSameType } from 'domain/usecases/surgical-order/upload-surgical-order-documents-same-type'
import { LoadFavoriteSurgicalOrders } from 'domain/usecases/surgical-order/load-favorite-surgical-orders'
import { DeleteFavoritedSurgicalOrder } from 'domain/usecases/surgical-order/delete-favorited-surgical-order'
import { SurgicalOrderCheckin } from 'domain/usecases/surgical-order/checkin'
import { UpdateSurgicalOrderRegenerateGuides } from 'domain/usecases/surgical-order/update-surgical-order-regenerate-guides'
import { LoadFavoriteDoctorSurgicalOrders } from 'domain/usecases/surgical-order/load-favorite-doctor-order'
import { UploadToMergeDocument } from 'domain/usecases/surgical-order/upload-to-merge-document'
import { ConfirmMergeDocuments } from 'domain/usecases/surgical-order/confirm-merge-documents'
import { DeleteUploadedDocuments } from 'domain/usecases/surgical-order/delete-uploaded-documents'
import { CountScheduledAndUnscheduledSurgicalOrders } from 'domain/usecases/surgical-order/scheduled-and-unscheduled-counter'
import { LoadGuidesByType } from 'domain/usecases/surgical-order/load-guides-by-type'
import { SearchSurgicalOrdersNew } from 'domain/usecases/surgical-order/search-surgical-orders-new'
import { CustomError, InternalServerError } from 'common/errors'
import parseParams from 'common/utils/parseParams'
import { LoadReasonsCancellation } from 'domain/usecases/surgical-order-cancellation/load-reasons-to-cancellation'
import { CancelSurgicalOrder } from 'domain/usecases/surgical-order-cancellation/cancel-surgical-order'
import { CheckGuidesGenerated } from 'domain/usecases/surgical-order/check-guides-generated'
import { RegenerateGuides } from 'domain/usecases/surgical-order/regenerate-guides'
import { GetSurgialOrderCounter } from 'domain/usecases/surgical-order/get-surgical-order-counter'
import { RemakeSurgicalOrder } from 'domain/usecases/surgical-order/remake-surgical-order'
import { FinishActiveSearchers } from 'domain/usecases/surgical-order-cancellation/finish-active-searcher'
import { GetFinalizeSurgicalOrdersHistory } from 'domain/usecases/surgical-order/get-finalize-surgical-orders-history'
import { EditFinalizeSurgicalOrder } from 'domain/usecases/surgical-order/edit-finalize-surgical-order'
import { CreateOrUpdateBudget } from 'domain/usecases/surgical-pendency/create-or-update-budget'
import { SearchSurgicalOrdersSchedules } from 'domain/usecases/surgical-order/search-surgical-orders-schedules'
import { UpdateSurgicalOrderClinicalRecord } from 'domain/usecases/surgical-order/update-surgical-order-clinical-record'
import { ProcessDocumentToMerge } from 'domain/usecases/surgical-order/process-document-to-merge'
import { ProcessDocumentCrmo } from 'domain/usecases/surgical-order/process-document-crmo'
import { MergeDocumentsCrmo } from 'domain/usecases/surgical-order/merge-documents-crmo'

export class SurgicalOrderRepository implements IReportsRepository {
  constructor(private readonly apiRepository: IApiRepository) {}
  async addPatientSurgicalOrder(
    params: AddSurgicalOrder.Params
  ): Promise<AddSurgicalOrder.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<AddSurgicalOrder.Model>

    const query = createPatientSurgicalOrderRequestMutation

    const httpResponse = await apiRepository.post({
      url: '/graphql',
      body: {
        query: query.query,
        variables: makeGraphQLVariable(params, 'data')
      },
      query: query.name
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as AddSurgicalOrder.Model
    }
  }

  async loadCid(params: LoadCid.Params): Promise<LoadCid.Model> {
    const apiRepository = this.apiRepository as IApiRepository<LoadCid.Model>

    const query = getCidQuery(params)

    const httpResponse = await apiRepository.post({
      url: '/graphql',
      body: {
        query: query.query,
        variables: {
          ...makeGraphQLVariable(params.pagination, 'pagination'),
          ...makeGraphQLVariable(params.query, 'query')
        }
      },
      query: query.name
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as LoadCid.Model
    }
  }

  async loadTuss(params: LoadTuss.Params): Promise<LoadTuss.Model> {
    const apiRepository = this.apiRepository as IApiRepository<LoadTuss.Model>

    const query = getTussQuery(params)

    const httpResponse = await apiRepository.post({
      url: '/graphql',
      body: {
        query: query.query,
        variables: {
          ...makeGraphQLVariable(params.pagination, 'pagination'),
          ...makeGraphQLVariable(params.name, 'name'),
          ...makeGraphQLVariable(params.code, 'code')
        }
      },
      query: query.name
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as LoadTuss.Model
    }
  }

  async loadSurgicalOrder(
    id: number,
    params: LoadSurgicalOrder.Params
  ): Promise<LoadSurgicalOrder.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<LoadSurgicalOrder.Model>

    const query = loadSurgicalOrderQuery(params)

    const httpResponse = await apiRepository.post({
      url: '/graphql',
      body: {
        query: query.query,
        variables: makeGraphQLVariable(id, 'surgical_order_id')
      },
      query: query.name
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as LoadSurgicalOrder.Model
    }
  }

  async loadSurgicalOrderStatus(
    params: LoadSurgicalOrderStatus.Params
  ): Promise<LoadSurgicalOrderStatus.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<LoadSurgicalOrderStatus.Model>

    const query = loadSurgicalOrderStatusQuery(params)

    const httpResponse = await apiRepository.post({
      url: '/graphql',
      body: {
        query: query.query,
        variables: makeGraphQLVariable(
          params.surgical_order_id,
          'surgical_order_id'
        )
      },
      query: query.name
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as LoadSurgicalOrderStatus.Model
    }
  }

  async searchSurgicalOrders(
    params: SearchSurgicalOrders.Params
  ): Promise<SearchSurgicalOrders.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<SearchSurgicalOrders.Model>

    const query = searchSurgicalOrdersQuery(params)
    delete params.params

    const { pagination, ...filters } = params

    const httpResponse = await apiRepository.post({
      url: '/graphql',
      body: {
        query: query.query,
        variables: {
          ...makeGraphQLVariable(filters, 'search'),
          ...makeGraphQLVariable(pagination, 'pagination')
        }
      },
      query: query.name
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as SearchSurgicalOrders.Model
    }
  }

  async searchSurgicalOrdersNew(
    params: SearchSurgicalOrdersNew.Params
  ): Promise<SearchSurgicalOrdersNew.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<SearchSurgicalOrdersNew.Model>

    const httpResponse: any = await apiRepository.get({
      url: '/surgical-order/search',
      params: params,
      paramsSerializer: (params: any) => parseParams(params)
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw new InternalServerError()
    } else {
      return httpResponse.body as SearchSurgicalOrdersNew.Model
    }
  }

  async registerSurgicalOrder(
    params: RegisterSurgicalOrder.Params
  ): Promise<RegisterSurgicalOrder.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<RegisterSurgicalOrder.Model>

    const query = registerSurgicalOrderMutation

    const httpResponse = await apiRepository.post({
      url: '/graphql',
      body: {
        query: query.query,
        variables: makeGraphQLVariable(params)
      },
      query: query.name
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as RegisterSurgicalOrder.Model
    }
  }

  async addFavorite(params: AddFavorite.Params): Promise<AddFavorite.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<AddFavorite.Model>

    const query = addFavoriteMutation

    const httpResponse = await apiRepository.post({
      url: '/graphql',
      body: {
        query: query.query,
        variables: makeGraphQLVariable(params)
      },
      query: query.name
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as AddFavorite.Model
    }
  }

  async uploadSurgicalOrderDocument(
    params: UploadSurgicalOrderDocument.Params
  ): Promise<UploadSurgicalOrderDocument.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<UploadSurgicalOrderDocument.Model>

    const httpResponse = await apiRepository.post({
      url: '/surgical-order/upload-document',
      body: params.form
    })

    if (httpResponse.body) {
      const { code } = httpResponse.body
      if (code) {
        throw handleGraphQLError(RepositoryErrors[code])
      }
    }

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.created
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as UploadSurgicalOrderDocument.Model
    }
  }

  async loadSurgicalOrderDocument(
    surgical_order_id: number | string,
    document_id: number
  ): Promise<LoadSurgicalOrderDocument.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<LoadSurgicalOrderDocument.Model>

    const httpResponse: any = await apiRepository.get(
      {
        url: `/surgical-order/${surgical_order_id}/document/${document_id}`
      },
      true
    )

    const contentType = httpResponse.headers['content-type']
    const data = httpResponse.data

    return {
      contentType,
      data
    }
  }

  async registerCrmoSurgicalOrder(
    params: RegisterSurgicalOrder.Params
  ): Promise<RegisterSurgicalOrder.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<RegisterSurgicalOrder.Model>

    const query = registerCrmoSurgicalOrderMutation

    const httpResponse = await apiRepository.post({
      url: '/graphql',
      body: {
        query: query.query,
        variables: makeGraphQLVariable(params)
      },
      query: query.name
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as RegisterSurgicalOrder.Model
    }
  }

  async changeSurgicalOrderStatus(
    params: ChangeSurgicalOrderStatus.Params
  ): Promise<ChangeSurgicalOrderStatus.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<ChangeSurgicalOrderStatus.Model>

    const query = updateSurgeryStatusMutation

    const httpResponse = await apiRepository.post({
      url: '/graphql',
      body: {
        query: query.query,
        variables: {
          surgical_order_id: params.surgical_order_id,
          ...makeGraphQLVariable(params.data)
        }
      },
      query: query.name
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as ChangeSurgicalOrderStatus.Model
    }
  }

  async reorderSurgicalOrder(
    params: ReorderSurgicalOrder.Params
  ): Promise<ReorderSurgicalOrder.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<ReorderSurgicalOrder.Model>

    const query = reorderSurgeryMutation

    const httpResponse = await apiRepository.post({
      url: '/graphql',
      body: {
        query: query.query,
        variables: makeGraphQLVariable(params)
      },
      query: query.name
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as ReorderSurgicalOrder.Model
    }
  }

  async updateSurgicalOrder(
    params: UpdateSurgicalOrder.Params
  ): Promise<UpdateSurgicalOrder.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<UpdateSurgicalOrder.Model>

    const query = updateSurgicalOrderMutation

    const httpResponse = await apiRepository.post({
      url: '/graphql',
      body: {
        query: query.query,
        variables: makeGraphQLVariable(params)
      },
      query: query.name
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body!
    }
  }

  async loadSurgeries(
    params: LoadSurgeries.Params
  ): Promise<LoadSurgeries.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<LoadSurgeries.Model>

    const query = loadSurgeriesQuery(params)

    const httpResponse = await apiRepository.post({
      url: '/graphql',
      body: {
        query: query.query,
        variables: {
          ...makeGraphQLVariable(params.pagination, 'pagination'),
          ...makeGraphQLVariable(params.name, 'name')
        }
      },
      query: query.name
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as LoadSurgeries.Model
    }
  }

  async countSurgicalOrdersByStatus(
    params: CountSurgicalOrdersByStatus.Params
  ): Promise<CountSurgicalOrdersByStatus.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<CountSurgicalOrdersByStatus.Model>

    const query = countSurgicalOrdersByStatus(params)

    const httpResponse = await apiRepository.post({
      url: '/graphql',
      body: {
        query: query.query,
        variables: {
          ...makeGraphQLVariable(
            { startDate: params.startDate, endDate: params.endDate },
            'period'
          )
        }
      },
      query: query.name
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as CountSurgicalOrdersByStatus.Model
    }
  }

  async concludeSurgicalOrder(
    params: ConcludeSurgicalOrder.Params
  ): Promise<ConcludeSurgicalOrder.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<ConcludeSurgicalOrder.Model>

    const query = concludeSurgicalOrderMutation

    const httpResponse = await apiRepository.post({
      url: '/graphql',
      body: {
        query: query.query,
        variables: {
          ...makeGraphQLVariable(params.data)
        }
      },
      query: query.name
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      if (httpResponse.statusCode === ApiStatusCode.conflict)
        throw new CustomError(
          'Não é possível reenviar sem alterações. Edite e tente novamente.'
        )
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as ConcludeSurgicalOrder.Model
    }
  }

  async deleteSurgicalOrderSolicitation(
    params: DeleteSurgicalOrderSolicitation.Params
  ): Promise<boolean> {
    const apiRepository = this
      .apiRepository as IApiRepository<DeleteSurgicalOrderSolicitation.Model>

    const query = deleteSurgicalOrderSolicitationMutation

    const httpResponse = await apiRepository.post({
      url: '/graphql',
      body: {
        query: query.query,
        variables: {
          ...makeGraphQLVariable(params.justification, 'justification'),
          ...makeGraphQLVariable(params.surgical_order_id, 'surgical_order_id')
        }
      },
      query: query.name
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as DeleteSurgicalOrderSolicitation.Model
    }
  }

  async checkin(
    params: SurgicalOrderCheckin.Params
  ): Promise<SurgicalOrderCheckin.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<SurgicalOrderCheckin.Model>

    const query = checkinMutation

    const httpResponse = await apiRepository.post({
      url: '/graphql',
      body: {
        query: query.query,
        variables: {
          ...makeGraphQLVariable(params.surgical_order_id, 'surgical_order_id')
        }
      },
      query: query.name
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as SurgicalOrderCheckin.Model
    }
  }

  async uploadSurgicalOrderDocumentsSameType(
    params: UploadSurgicalOrderDocumentsSameType.Params
  ): Promise<UploadSurgicalOrderDocumentsSameType.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<UploadSurgicalOrderDocumentsSameType.Model>

    const httpResponse = await apiRepository.post({
      url: '/surgical-order/upload-same-type-documents',
      body: params.form
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.created
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as UploadSurgicalOrderDocumentsSameType.Model
    }
  }

  async updateSurgicalOrderRegenerateGuides(
    params: UpdateSurgicalOrderRegenerateGuides.Params
  ): Promise<UpdateSurgicalOrderRegenerateGuides.Model> {
    const solicitations = params?.opme?.solicitations?.map((solicitation) => ({
      description: solicitation.description,
      quantity: Number(solicitation.quantity)
    }))

    const solicitationsFilter = solicitations === undefined ? [] : solicitations
    const opme =
      params.opme.providers === undefined ? { providers: [] } : params.opme

    const apiRepository = this
      .apiRepository as IApiRepository<UpdateSurgicalOrderRegenerateGuides.Model>

    const query = updateSurgicalOrderRegenerateGuidesMutation

    const httpResponse = await apiRepository.post({
      url: '/graphql',
      body: {
        query: query.query,
        variables: {
          ...makeGraphQLVariable(
            {
              healthInsurance: {
                ...params.healthInsurance,
                health_insurance_id: params.healthInsurance.healthInsuranceCode
              },
              surgical_order_id: params.surgical_order_id,
              patient: {
                name: params.patient.name,
                gender: params.patient.gender,
                birthday: params.patient.birthday,
                email: params.patient.email || '',
                phone: params.patient.phone || '',
                landlinePhone: params.patient.landlinePhone
              },
              opme: { ...opme, solicitations: solicitationsFilter }
            },
            'input'
          )
        }
      },
      query: query.name
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as UpdateSurgicalOrderRegenerateGuides.Model
    }
  }

  async registerSecretarySurgicalOrder(
    params: RegisterSurgicalOrder.Params
  ): Promise<RegisterSurgicalOrder.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<RegisterSurgicalOrder.Model>
    const query = registerSecretarySurgicalOrderMutation
    const httpResponse = await apiRepository.post({
      url: '/graphql',
      body: {
        query: query.query,
        variables: makeGraphQLVariable(params)
      },
      query: query.name
    })
    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as RegisterSurgicalOrder.Model
    }
  }

  async loadFavoriteSurgicalOrders(
    params: LoadFavoriteSurgicalOrders.Params
  ): Promise<LoadFavoriteSurgicalOrders.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<LoadFavoriteSurgicalOrders.Model>

    const query = loadFavoriteSurgicalOrdersQuery(params)

    const httpResponse = await apiRepository.post({
      url: '/graphql',
      body: {
        query: query.query,
        variables: makeGraphQLVariable(params)
      },
      query: query.name
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as LoadFavoriteSurgicalOrders.Model
    }
  }

  async loadFavoriteDoctorSurgicalOrders(
    params: LoadFavoriteDoctorSurgicalOrders.Params
  ): Promise<LoadFavoriteDoctorSurgicalOrders.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<LoadFavoriteDoctorSurgicalOrders.Model>

    const query = loadFavoriteDoctorOrdersQuery(params)

    const httpResponse = await apiRepository.post({
      url: '/graphql',
      body: {
        query: query.query,
        variables: makeGraphQLVariable(params)
      },
      query: query.name
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as LoadFavoriteSurgicalOrders.Model
    }
  }

  async deleteFavoritedSurgicalOrder(
    params: DeleteFavoritedSurgicalOrder.Params
  ): Promise<DeleteFavoritedSurgicalOrder.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<DeleteFavoritedSurgicalOrder.Model>

    const query = deleteFavoritedSurgicalOrderMutation

    const httpResponse = await apiRepository.post({
      url: '/graphql',
      body: {
        query: query.query,
        variables: makeGraphQLVariable(params.data, 'data')
      },
      query: query.name
    })

    if (!httpResponse.body) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as DeleteFavoritedSurgicalOrder.Model
    }
  }

  async uploadToMergeDocument(
    params: UploadToMergeDocument.Params
  ): Promise<UploadToMergeDocument.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<UploadToMergeDocument.Model>
    const form = new FormData()
    form.append('file', params.file)
    form.append('group_id', params.group_id)
    form.append('type', params.type)
    const httpResponse = await apiRepository.post({
      url: '/surgical-order/upload-document-to-merge',
      body: form
    })

    if (httpResponse.body) {
      const { code } = httpResponse.body
      if (code) {
        throw handleGraphQLError(RepositoryErrors[code])
      }
    }

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.created
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as UploadToMergeDocument.Model
    }
  }

  async confirmMergeDocuments(
    params: ConfirmMergeDocuments.Params
  ): Promise<boolean> {
    const apiRepository = this
      .apiRepository as IApiRepository<ConfirmMergeDocuments.Model>

    const query = confirmMergeDocumentsMutation

    const httpResponse = await apiRepository.post({
      url: '/graphql',
      body: {
        query: query.query,
        variables: {
          ...makeGraphQLVariable(params.group_id, 'group_id'),
          ...makeGraphQLVariable(params.surgical_order_id, 'surgical_order_id')
        }
      },
      query: query.name
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as ConfirmMergeDocuments.Model
    }
  }

  async deleteUploadedDocuments(
    params: DeleteUploadedDocuments.Params
  ): Promise<boolean> {
    const apiRepository = this
      .apiRepository as IApiRepository<DeleteUploadedDocuments.Model>

    const query = deleteUploadedDocumentsMutation

    const httpResponse = await apiRepository.post({
      url: '/graphql',
      body: {
        query: query.query,
        variables: makeGraphQLVariable(params.document_id, 'document_id')
      },
      query: query.name
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as DeleteUploadedDocuments.Model
    }
  }

  async countScheduledAndUnscheduledSurgicalOrders(
    params: CountScheduledAndUnscheduledSurgicalOrders.Params
  ): Promise<CountScheduledAndUnscheduledSurgicalOrders.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<CountScheduledAndUnscheduledSurgicalOrders.Model>

    const query = countScheduledAndUnscheduledSurgicalOrders(params)

    const httpResponse = await apiRepository.post({
      url: '/graphql',
      body: {
        query: query.query,
        variables: {
          ...makeGraphQLVariable(
            {
              startDate: params.startDate,
              endDate: params.endDate
            },
            'period'
          )
        }
      },
      query: query.name
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as CountScheduledAndUnscheduledSurgicalOrders.Model
    }
  }

  async loadGuidesByType(
    surgical_order_id: number,
    type: string,
    params: LoadGuidesByType.Params
  ): Promise<LoadGuidesByType.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<LoadGuidesByType.Model>

    const query = loadGuidesByType(params)

    const httpResponse = await apiRepository.post({
      url: '/graphql',
      body: {
        query: query.query,
        variables: {
          ...makeGraphQLVariable(surgical_order_id, 'surgical_order_id'),
          ...makeGraphQLVariable(type, 'type')
        }
      },
      query: query.name
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as LoadGuidesByType.Model
    }
  }

  async createSurgicalOrderStatus(
    params: CreateSurgicalOrderStatus.Params
  ): Promise<CreateSurgicalOrderStatus.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<CreateSurgicalOrderStatus.Model>

    const httpResponse: any = await apiRepository.post({
      url: `/surgical-order/status`,
      body: params
    })

    return httpResponse.body as unknown as CreateSurgicalOrderStatus.Model
  }

  async loadReasonsCancellation(): Promise<LoadReasonsCancellation.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<LoadReasonsCancellation.Model>

    const httpResponse: any = await apiRepository.get({
      url: '/surgical-cancellation/reason'
    })

    if (httpResponse && httpResponse.statusCode !== ApiStatusCode.ok) {
      throw new InternalServerError()
    } else {
      return httpResponse.body as LoadReasonsCancellation.Model
    }
  }

  async cancelSurgicalOrder(
    params: CancelSurgicalOrder.Params
  ): Promise<CancelSurgicalOrder.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<CancelSurgicalOrder.Model>

    const httpResponse: any = await apiRepository.put({
      url: `/surgical-order/cancel/cancel-surgical-order`,
      body: params
    })

    return httpResponse.body as CancelSurgicalOrder.Model
  }

  async checkGuidesGenerated(
    params: CheckGuidesGenerated.Params
  ): Promise<CheckGuidesGenerated.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<CheckGuidesGenerated.Model>

    const httpResponse: any = await apiRepository.get({
      url: `/surgical-order/guides/check/${params.surgical_order_id}`,
      body: params
    })

    return httpResponse.body.data as CheckGuidesGenerated.Model
  }

  async regenerateGuides(params: RegenerateGuides.Params) {
    const apiRepository = this
      .apiRepository as IApiRepository<RegenerateGuides.Model>

    const httpResponse: any = await apiRepository.post({
      url: `/surgical-order/guides/generate/types`,
      body: params
    })

    return httpResponse.body.data as RegenerateGuides.Model
  }

  async getSurgicalOrderCounter(params: GetSurgialOrderCounter.Params) {
    const apiRepository = this
      .apiRepository as IApiRepository<GetSurgialOrderCounter.Model>

    const httpResponse: any = await apiRepository.get({
      url: `/surgical-order/hospital/dashboard/counters?start_date=${params.startDate}&end_date=${params.endDate}`,
      params: params
    })

    if (httpResponse && httpResponse.statusCode !== ApiStatusCode.ok) {
      throw new InternalServerError()
    } else {
      return httpResponse.body as GetSurgialOrderCounter.Model
    }
  }

  async remakeSurgicalOrder(
    params: RemakeSurgicalOrder.Params
  ): Promise<RemakeSurgicalOrder.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<RemakeSurgicalOrder.Model>

    const httpResponse = await apiRepository.post({
      url: '/surgical-order/remake',
      body: params
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.created
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as RemakeSurgicalOrder.Model
    }
  }

  async finishActiveSearchers(
    params: FinishActiveSearchers.Params
  ): Promise<FinishActiveSearchers.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<FinishActiveSearchers.Model>

    const httpResponse: any = await apiRepository.post({
      url: `/active-searchers/finish/${params.surgical_order_id}`,
      body: params
    })

    if (httpResponse && httpResponse.statusCode !== ApiStatusCode.ok) {
      throw new InternalServerError()
    } else {
      return httpResponse.body as FinishActiveSearchers.Model
    }
  }

  async getFinalizeSurgicalOrdersHistory(
    params: GetFinalizeSurgicalOrdersHistory.Params
  ) {
    const apiRepository = this
      .apiRepository as IApiRepository<GetFinalizeSurgicalOrdersHistory.Model>

    const httpResponse: any = await apiRepository.get({
      url: `/surgical-orders/${params.surgical_order_id}/finalize-surgical-orders`,
      params: params
    })

    if (httpResponse && httpResponse.statusCode !== ApiStatusCode.ok) {
      throw new InternalServerError()
    } else {
      return httpResponse.body as GetFinalizeSurgicalOrdersHistory.Model
    }
  }

  async editFinalizeSurgicalOrder(params: EditFinalizeSurgicalOrder.Params) {
    const apiRepository = this
      .apiRepository as IApiRepository<EditFinalizeSurgicalOrder.Model>

    const httpResponse: any = await apiRepository.post({
      url: `/surgical-orders/${params.form.get(
        'surgical_order_id'
      )}/finalize-surgical-orders`,
      body: params.form
    })

    if (httpResponse && httpResponse.statusCode !== ApiStatusCode.created) {
      throw new InternalServerError()
    } else {
      return httpResponse.body as EditFinalizeSurgicalOrder.Model
    }
  }

  async createOrUpdateBudget(
    params: CreateOrUpdateBudget.Params
  ): Promise<CreateOrUpdateBudget.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<CreateOrUpdateBudget.Model>

    const httpResponse: any = await apiRepository.post({
      url: `/surgical-orders/${params.surgical_order_id}/budgets`,
      body: {
        value: params.value
      }
    })
    if (
      !httpResponse.statusCode ||
      httpResponse.statusCode !== ApiStatusCode.created
    ) {
      throw new CustomError('Erro ao atualizar o orçamento')
    } else {
      return httpResponse.body as CreateOrUpdateBudget.Model
    }
  }

  async searchSurgicalOrdersSchedules(
    params: SearchSurgicalOrdersSchedules.Params
  ): Promise<SearchSurgicalOrdersSchedules.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<SearchSurgicalOrdersSchedules.Model>

    const httpResponse: any = await apiRepository.get({
      url: '/surgical-order/schedules',
      params: params,
      paramsSerializer: (params: any) => parseParams(params)
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw new InternalServerError() // ToDo: Tratativa de erro
    } else {
      return httpResponse.body as SearchSurgicalOrdersSchedules.Model
    }
  }

  async updateSurgicalOrderClinicalRecord(
    surgical_order_id: number,
    params: UpdateSurgicalOrderClinicalRecord.Params
  ): Promise<UpdateSurgicalOrderClinicalRecord.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<UpdateSurgicalOrderClinicalRecord.Model>

    const httpResponse: any = await apiRepository.put({
      url: `/surgical-order/${surgical_order_id}/clinical-record`,
      body: params
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.noContent
    ) {
      throw new CustomError('Ocorreu um erro ao atualizar o pedido cirúrgico')
    } else {
      return httpResponse.body as UpdateSurgicalOrderClinicalRecord.Model
    }
  }

  async processDocumentToMerge(
    params: ProcessDocumentToMerge.Params
  ): Promise<ProcessDocumentToMerge.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<ProcessDocumentToMerge.Model>

    const httpResponse = await apiRepository.post({
      url: '/surgical-order/process-document-to-merge',
      body: params
    })

    if (
      httpResponse.statusCode !== ApiStatusCode.ok &&
      httpResponse.statusCode !== ApiStatusCode.created
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    }

    return httpResponse.body as ProcessDocumentToMerge.Model
  }

  async processDocumentCrmo(
    params: ProcessDocumentCrmo.Params
  ): Promise<ProcessDocumentCrmo.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<ProcessDocumentCrmo.Model>

    const httpResponse = await apiRepository.post({
      url: '/surgical-order/crmo/process-document',
      body: params
    })

    if (
      httpResponse.statusCode !== ApiStatusCode.ok &&
      httpResponse.statusCode !== ApiStatusCode.created
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    }

    return httpResponse.body as ProcessDocumentCrmo.Model
  }

  async mergeDocumentsCrmo(
    params: MergeDocumentsCrmo.Params
  ): Promise<MergeDocumentsCrmo.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<MergeDocumentsCrmo.Model>

    const httpResponse = await apiRepository.post({
      url: '/surgical-order/crmo/merge-documents',
      body: params
    })

    if (
      httpResponse.statusCode !== ApiStatusCode.ok &&
      httpResponse.statusCode !== ApiStatusCode.created
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    }

    return httpResponse.body as MergeDocumentsCrmo.Model
  }
}
